import {inject, Injectable} from '@angular/core';
import { WindowRefService } from '@services/window-ref-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppConfig } from 'client/app/app.config';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { ExperimentService } from '@services/experiment';
import { AovResponse, CartPricingResult, CartUpdateEventParams, CartUpdateOptions, CartUpdateResult } from '@shared/types/cart.types';
import { ApiClientConstant } from '@cureskin/api-client';
import { ActivePregnancyTag, UserService } from '@services/user/user.service';
import { ValueOf } from '@cureskin/api-client/src/common';
import { Catalog } from '@cureskin/api-client/src/server-api';
import { User } from 'parse';


import {ActivatedRoute, Router} from '@angular/router';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {Store} from '@ngrx/store';
import {ImageService} from '@services/image-service';
import {VariantsPopupComponent} from 'client/app/user/user-view/user-home/variants-popup/variants-popup.component';
import {toPlainObject, findSmallestMargUnitVariant, buildVariantDataList, resolveProductImage, resolveProductUnit} from 'client/helper/variants-popup-helper';
import * as fromActions from '@store/actions/app.actions';
import { Entity, EntityEnum } from '@cureskin/api-client-rn';

/** A product delivered within this many days is treated as already bought and is not re-added. */
const RECENT_DELIVERY_WINDOW_DAYS = 30;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

@Injectable({providedIn: 'root'})
export class CartService {
  private readonly userService: UserService = inject(UserService);
  private readonly appConfig: AppConfig = inject(AppConfig);
  private readonly window: WindowRefService = inject(WindowRefService);
  private readonly eventLoggerService: EventLoggerService = inject(EventLoggerService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly experimentService: ExperimentService = inject(ExperimentService);

  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly router: Router = inject(Router);
  private readonly imageService: ImageService = inject(ImageService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly store: Store = inject(Store);

  private cart: any;
  private cartItems: any[] = [];
  private originalCashBalance: number = 0;

  // Skip variants check config: 'from' param for reorder path, 'section' for other paths
  private readonly skipVariantsCheckForFromParams: string[] = ['bogoProduct'];
  private readonly skipVariantsCheckForSections: string[] = ['saleProducts'];

  constructor() {}

  async addOrRemoveFromCart(
    product: any,
    action: string,
    options: CartUpdateOptions
  ): Promise<CartUpdateResult> {
    const limit = product.prescriptionRequired
      ? this.appConfig.Shared.Product.CartLimits.RX_LIMIT
      : this.appConfig.Shared.Product.CartLimits.NON_RX_LIMIT;

    // Use passed cartItems if available, otherwise fetch from server
    let currentCartItems: any[] = [];
    if (options.cartItems && options.cartItems.length > 0) {
      currentCartItems = options.cartItems;
    } else {
      // Fallback: fetch cart only if cartItems not provided (e.g., wallet-view, product-controller)
      const currentCart = await this.conn.fetchNewCart(this.conn.user.id);
      currentCartItems = currentCart?.data?.lineItems || [];
    }
    const currentCartItem = currentCartItems.find((item: any): any => item.productId === product.objectId);
    let quantity = currentCartItem?.quantity ?? 0;

    // For REMOVE action, if product not found in passed cartItems, fetch from server to ensure accuracy
    if (action === this.appConfig.Shared.Cart.Action.REMOVE && quantity === 0 && options.cartItems && options.cartItems.length > 0) {
      // Product might have been added/removed elsewhere, fetch fresh cart data
      const currentCart = await this.conn.fetchNewCart(this.conn.user.id);
      currentCartItems = currentCart?.data?.lineItems || [];
      const freshCartItem = currentCartItems.find((item: any): any => item.productId === product.objectId);
      quantity = freshCartItem?.quantity ?? 0;
    }
    const eventParams = {
      objectId: product?.objectId,
      quantity: quantity,
      action: action,
      tag: options?.tag || 'no-tag',
      eventName: options?.eventName
    };
    if (action === this.appConfig.Shared.Cart.Action.ADD) {
      // Skip guard check if already done by caller (e.g., in component)
      let isOverriddenByDiscontinuedFlow = false;
      if (!options.skipGuardCheck) {
        const guardResult = await this.checkProductAllowedForCartAndPrompt(product, options);
        // Defensive: treat any non-true value (undefined/null/false) as a cancel and do not add
        if (!guardResult || guardResult.allowed !== true) {
          return this.createFailureResult(quantity, options.productsInCart);
        }
        if (guardResult.overrideProduct) {
          product = guardResult.overrideProduct;
          isOverriddenByDiscontinuedFlow = true;
          options.skipVariantsCheck = true;
        }
      }
      // Skip variants check when adding via bottomsheet (e.g. variants-popup), specific pages,
      // or when product was overridden by the discontinued popup flow
      if (!isOverriddenByDiscontinuedFlow && !this.shouldSkipVariantsCheck(options)) {
        const variantsResult = await this.checkVariantsAndShowPopupIfNeeded(product, options);
        if (variantsResult.shouldShowPopup) {
          return this.createFailureResult(quantity, options.productsInCart);
        }
      }
      // Check limit using current quantity (from passed cartItems or server)
      if (quantity >= limit) {
        this.broadcast.broadcast('NOTIFY', {
          message: `Maximum quantity is limited to ${limit} per product`
        });
        return this.createFailureResult(quantity, options.productsInCart);
      }
      this.sendCartUpdateEvent(eventParams);
      return this.handleAddProduct(product, quantity, options);
    }

    if (action === this.appConfig.Shared.Cart.Action.REMOVE && quantity > 0) {
      this.sendCartUpdateEvent(eventParams);
      return this.handleRemoveProduct(product, quantity, options);
    }

    return this.createFailureResult(quantity, options.productsInCart);
  }

  private async checkVariantsAndShowPopupIfNeeded(
    product: any,
    options: CartUpdateOptions,
  ): Promise<{ shouldShowPopup: boolean }> {
    try {
      if (product?.isSaleProduct) return { shouldShowPopup: false };

      const mainProductId =
        product?.mainProduct?.objectId ?? product?.mainProduct?.id ?? product?.objectId;
      if (!mainProductId) return { shouldShowPopup: false };

      const { variants } = await this.conn.getVariantsByMainProductId(mainProductId);
      if (!variants || variants.length <= 1) return { shouldShowPopup: false };

      await this.openVariantsPopup(product, variants, options.cartItems, options.section);
      return { shouldShowPopup: true };
    } catch {
      return { shouldShowPopup: false };
    }
  }

  async openVariantsPopup(
    product: any,
    variants: any[],
    cartItems?: any[],
    section?: string,
  ): Promise<void> {
    // Refresh from the new cart (the source of truth for cart mutations) so the popup's quantities
    // reflect host-page add/removes on home & shop. The cached this.cartItems and the caller-passed
    // cartItems can both be stale (e.g. host refreshes from the legacy cart).
    try {
      await this.syncCartWithStore();
    } catch {
      // fall back to cached / passed-in cart items below
    }
    const cartItemsList = this.cartItems.length ? this.cartItems : (cartItems || []);
    const productJson = toPlainObject(product);
    const smallestMargUnitVariant = findSmallestMargUnitVariant(variants);
    const variantData = buildVariantDataList(variants, cartItemsList, productJson.objectId, section);
    const productImage = resolveProductImage(productJson, smallestMargUnitVariant, (url) => this.imageService.getURL(url));
    const unit = resolveProductUnit(productJson, smallestMargUnitVariant);

    const bannerData = {
      title: productJson.title ?? smallestMargUnitVariant?.title ?? '',
      productImage,
      variantData,
      unit,
    };

    const mainProductId = product?.mainProduct?.objectId ?? product?.mainProduct?.id ?? product?.objectId;

    this.bottomSheet.open(VariantsPopupComponent, {
      data: {
        panelClass: 'variants-popup-panel',
        bannerData,
        mainProductId,
        section,
        experimentVariantData: { variant: { uiVariants: { showProductPopup: true } } },
        callbacks: {
          closeBottomSheet: () => this.bottomSheet.dismiss(),
          addOrRemoveProductToCart: (productId: string, action: 'ADD' | 'REMOVE') =>
            this.addOrRemoveProductFromVariantsPopup(productId, action),
          handleProceedToBuy: (productTitle: string) => this.handleVariantsPopupProceedToBuy(productTitle, section),
        },
      },
    });
  }

  private async addOrRemoveProductFromVariantsPopup(productId: string, action: 'ADD' | 'REMOVE'): Promise<void> {
    try {
      await this.executeCartAction(productId, action);
      await this.syncCartWithStore();
      this.broadcast.broadcast('CART_UPDATED', { productId, action: action.toLowerCase() });
      // Refresh the host page's cart / "complete your order" banner, as shop sections do on add.
      this.broadcast.broadcast('REFRESH_SHOP_CART');
    } catch (error: any) {
      const errorMessage = error?.message ?? `Failed to ${action.toLowerCase()} product to cart`;
      this.broadcast.broadcast('NOTIFY', { message: errorMessage });
      throw error;
    }
  }

  private async executeCartAction(productId: string, action: 'ADD' | 'REMOVE'): Promise<void> {
    if (action === 'ADD') {
      await this.conn.addProductToNewCart({ productId, quantity: 1, section: 'variantsPopup' });
    } else {
      await this.conn.removeProductFromNewCart({ productId, quantity: 1 });
    }
  }

  private async syncCartWithStore(): Promise<void> {
    const updatedCart = await this.conn.fetchNewCart(this.conn.user.id);
    this.cartItems = updatedCart?.data?.lineItems || [];
    const cartProductQuantities: { [key: string]: number } = {};
    this.cartItems.forEach((item: any) => {
      cartProductQuantities[item.productId] = item.quantity;
    });
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: cartProductQuantities }));
  }

  private handleVariantsPopupProceedToBuy(productTitle: string, section?: string): void {
    this.bottomSheet.dismiss();
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
      cta: 'variants_popup_proceed_to_buy',
      product_title: productTitle,
      section: section ?? 'unknown',
    }));
    this.router.navigate(['cart']);
  }


  async checkProductAllowedForCartAndPrompt(product: any, options?: CartUpdateOptions): Promise<{ allowed: boolean; overrideProduct?: any }> {
    try {
      const user: User = this.conn.getActingUser();
      const age = user?.get('Age') ? Number.parseInt(user?.get('Age'), 10) : null;
      const marketingTags = user.get('marketingTags') || [];
      const overridePregnancyCheck = options?.overridePregnancyCheck ?? false;
      const section = options?.section ?? '';

      const shouldShowTeenagerBottomSheet = this.shouldShowTeenagerBottomSheet({
        age,
        safteyProfile: product?.safteyProfile,
        section
      });

      if (shouldShowTeenagerBottomSheet) {
        this.broadcast.broadcast('OPEN_TEENAGER_RESTRICTION_BOTTOMSHEET', { product });
        return { allowed: false };
      }

      const pregnancyResult = this.shouldShowPregnancyBottomSheet({
        marketingTags,
        gender: user?.get('Gender'),
        safteyProfile: product?.safteyProfile,
        overridePregnancyCheck,
        section
      });

      if (pregnancyResult.show) {
        const { navigateToCartOnAdd, navigateToArticlePage, section } = options || {};
        this.broadcast.broadcast('OPEN_PREGNANCY_RESTRICTION_BOTTOMSHEET', {
          product,
          activeTag: pregnancyResult.activeTag,
          ...(navigateToCartOnAdd && { navigateToCartOnAdd }),
          ...(navigateToArticlePage && { navigateToArticlePage }),
          ...(section && { section }),
        });
        return { allowed: false };
      }

      const overrideApprovalCheck = options?.overrideApprovalCheck ?? false;
      const shouldShowApproval = this.shouldShowApprovalBottomSheet({
        requiresApproval: product?.requiresApproval,
        overrideApprovalCheck,
        section,
      });

      if (shouldShowApproval) {
        const { navigateToCartOnAdd, navigateToArticlePage, section } = options || {};
        this.broadcast.broadcast('OPEN_APPROVAL_BOTTOMSHEET', {
          product,
          ...(navigateToCartOnAdd && { navigateToCartOnAdd }),
          ...(navigateToArticlePage && { navigateToArticlePage }),
          ...(section && { section }),
        });
        return { allowed: false };
      }

      if (options?.skipDiscontinuedCheck) return { allowed: true };

      const status = product?.inventoryStatus;
      const { DISCONTINUED, RESTRICTED } = this.appConfig.Shared.Inventory.Type;
      const isBlocked = [DISCONTINUED, RESTRICTED].includes(status);
      if (!isBlocked) return { allowed: true };

      const catalog = await this.conn.findCatalogWithAlternateProduct(product.objectId);
      const alternate = catalog?.get ? catalog.get('alternateProduct') : catalog?.alternateProduct;

      // If no alternate product exists, show info popup and block
      if (!alternate) {
        this.broadcast.broadcast('OPEN_DISCONTINUED_INFO', { product });
        return { allowed: false };
      }
      const alternateJson = JSON.parse(JSON.stringify(alternate));
      const altStatus = alternateJson?.inventoryStatus; // parsing parse object to json
      const altBlocked = [DISCONTINUED, RESTRICTED].includes(altStatus);
      const altPrice = alternateJson?.price ?? 0;
      const hasEligibleAlternate = !!alternate && !altBlocked && altPrice > 0;
      // Show popup via global host using existing <pop-up> + DiscontinuedPopupComponent
      if (hasEligibleAlternate) {
        return await new Promise((resolve): void => {
          let resolved = false;
          // Safety timeout: if popup is closed without explicit action, treat as cancel
          // This should never trigger if callbacks work correctly, but provides a safety net
          const timeout = setTimeout((): void => {
            if (!resolved) {
              resolved = true;
              resolve({ allowed: false });
            }
          }, 10000); // 10 second timeout (safety net in case callbacks fail)

          const onConfirmHandler = (): void => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              resolve({ allowed: true, overrideProduct: alternateJson });
            }
          };

          const onCancelHandler = (): void => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              resolve({ allowed: false });
            }
          };

          this.broadcast.broadcast('OPEN_DISCONTINUED_POPUP', {
            oldProduct: product,
            newProduct: alternateJson,
            onConfirm: onConfirmHandler,
            onCancel: onCancelHandler
          });
        });
      }

      // No eligible alternate → show info popup and block
      this.broadcast.broadcast('OPEN_DISCONTINUED_INFO', { product });
      return { allowed: false };
    } catch (err) {
      // Fail safe: if anything goes wrong, block to avoid bad cart state
      return { allowed: false };
    }
  }

  private sendCartUpdateEvent(eventParams: CartUpdateEventParams): void {
    if (!eventParams.eventName) {
      return;
    }

    const removedEvents = ['HP_recently_viewed_cart_update', 'SP_recently_viewed_cart_update']
    const channels = removedEvents.includes(eventParams.eventName) ? [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ] : []

    this.eventLoggerService.cleverTapEvent(eventParams.eventName, JSON.stringify(eventParams), channels);
  }

  async loadCartProductsIntoStore(): Promise<Array<{ productId: string; quantity: number }>> {
    const cartData = await this.getCartAndPricingDetails(0);
    const cartItems = cartData.cartItems ?? [];

    const cartProductQuantities: { [key: string]: number } = {};
    cartItems.forEach(item => {
      cartProductQuantities[item.productId] = item.quantity;
    });
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: cartProductQuantities }));
    return cartItems;
  }

  async forceUpdateProductInCart(
    product: Catalog,
    action: string,
    options: {
      img?: HTMLImageElement;
      productsInCart: { [key: string]: number };
      cartItems?: Array<{ productId: string; quantity: number }>;
      section: string;
      flyToSelector?: string;
    },
  ): Promise<CartUpdateResult> {
    const cartOptions: CartUpdateOptions = {
      img: options.img,
      productsInCart: options.productsInCart,
      cartItems: options.cartItems,
      section: options.section,
      flyToSelector: options.flyToSelector,
      skipGuardCheck: true,
      skipVariantsCheck: true,
    };
    return this.addOrRemoveFromCart(product, action, cartOptions);
  }

  /**
   * Guarantees each given product is present in the cart. A product delivered to the user in the
   * last `RECENT_DELIVERY_WINDOW_DAYS` days is dropped first; whatever survives is added at one
   * quantity unless it is already in the cart, in which case the existing line is left untouched
   * (never incremented). Fails soft — a product that cannot be added is skipped rather than
   * aborting the rest. Returns the ids that were actually added.
   */
  async ensureProductsInCart(products: Catalog[], section: string): Promise<string[]> {
    const uniqueProducts = this.dedupeProductsById(products);
    if (!uniqueProducts.length) return [];

    const addedProductIds: string[] = [];
    try {
      const recentlyDeliveredProductIds = await this.getRecentlyDeliveredProductIds(RECENT_DELIVERY_WINDOW_DAYS);
      const notRecentlyDelivered = uniqueProducts
        .filter((product: Catalog): boolean => !recentlyDeliveredProductIds.has(product.objectId));
      if (!notRecentlyDelivered.length) return [];

      const cartItems = await this.loadCartProductsIntoStore();
      let productsInCart = this.toProductsInCart(cartItems);

      for (const product of notRecentlyDelivered) {
        if (productsInCart[product.objectId] > 0) continue;
        const result = await this.forceUpdateProductInCart(product, this.appConfig.Shared.Cart.Action.ADD, {
          productsInCart,
          cartItems,
          section,
        });
        if (!result?.success) continue;
        productsInCart = result.updatedProductsInCart ?? productsInCart;
        addedProductIds.push(product.objectId);
      }

      if (addedProductIds.length) await this.loadCartProductsIntoStore();
    } catch (error) {
      this.eventLoggerService.trackEvent('ensure_products_in_cart_failed', { section, error: `${error}` });
    }
    return addedProductIds;
  }

  /**
   * Product ids the user actually received within the last `withinDays` days — orders that reached
   * the DELIVERED stage, dated by `deliveredOn` rather than when the order was placed. An order
   * still in transit does not count, so a product the user has not received yet can still be
   * re-added. Fails soft to an empty set so a failing order lookup degrades to the cart-only check
   * rather than blocking every add.
   */
  private async getRecentlyDeliveredProductIds(withinDays: number): Promise<Set<string>> {
    const recentlyDeliveredProductIds = new Set<string>();
    try {
      const orders = await this.conn.getOrders({ stages: [EntityEnum.Order.Stage.DELIVERED] });
      const cutoff = Date.now() - (withinDays * MILLISECONDS_PER_DAY);

      (orders || []).forEach((order: Entity['Order']): void => {
        if (order?.stage !== EntityEnum.Order.Stage.DELIVERED) return;
        const deliveredAt = new Date(order?.deliveredOn ?? '').getTime();
        if (!Number.isFinite(deliveredAt) || deliveredAt < cutoff) return;
        (order?.products || []).forEach((product: Catalog): void => {
          if (product?.objectId) recentlyDeliveredProductIds.add(product.objectId);
        });
      });
    } catch (error) {
      this.eventLoggerService.trackEvent('recent_orders_lookup_failed', { error: `${error}` });
    }
    return recentlyDeliveredProductIds;
  }

  private dedupeProductsById(products: Catalog[]): Catalog[] {
    const seen = new Set<string>();
    return (products || []).filter((product: Catalog): boolean => {
      if (!product?.objectId || seen.has(product.objectId)) return false;
      seen.add(product.objectId);
      return true;
    });
  }

  private toProductsInCart(cartItems: Array<{ productId: string; quantity: number }>): { [key: string]: number } {
    return (cartItems || []).reduce((quantities: { [key: string]: number }, item): { [key: string]: number } => {
      quantities[item.productId] = item.quantity;
      return quantities;
    }, {});
  }

  async getCartAndPricingDetails(
    cashBalance: number,
    resetCashBalance: boolean = true
  ): Promise<CartPricingResult> {
    if (resetCashBalance) {
      this.originalCashBalance = cashBalance;
    }

    this.cart = await this.conn.fetchNewCart(this.conn.user.id);
    this.cartItems = this.cart?.data?.lineItems || [];
    const aov = this.cart?.data?.aov;

    if (this.cartItems.length === 0) {
      return this.handleEmptyCart(aov);
    }

    const pricingDetails = this.calculateDiscountForItems(this.originalCashBalance, this.cartItems);
    return { ...pricingDetails, aov, cartItems: this.cartItems };
  }

  animateAddToCart(img?: HTMLImageElement, flyToSelector?: string): void {
    if (!img) return;

    const image = new Image();
    image.src = img.src;
    Object.assign(image.style, {
      position: 'absolute',
      top: `${img.getBoundingClientRect().top}px`,
      right: `${this.window.nativeWindow.innerWidth - img.getBoundingClientRect().right}px`,
      height: `${img.offsetHeight}px`,
      width: 'auto',
      zIndex: '1000'
    });

    if (flyToSelector) {
      const target = this.window.nativeWindow.document.querySelector(flyToSelector) as HTMLElement | null;
      if (target) {
        const rect = target.getBoundingClientRect();
        image.style.setProperty('--fly-end-top', `${rect.top}px`);
        image.style.setProperty('--fly-end-right', `${this.window.nativeWindow.innerWidth - rect.right}px`);
      }
    }

    image.classList.add('fly-animation');
    image.onanimationend = () => image.remove();
    this.window.nativeWindow.document.body.appendChild(image);
  }

  private shouldSkipVariantsCheck(options: CartUpdateOptions): boolean {
    if (options.skipVariantsCheck) return true;

    if (!this.experimentService.addToCartVariantSelection) return true;

    const currentUrl = this.router.url;
    const isReorderPath = currentUrl.includes('/user/order/reorder');

    if (isReorderPath) {
      // On reorder path, check 'from' query param
      const fromParam = this.activatedRoute.snapshot.queryParams['from'];
      return this.skipVariantsCheckForFromParams.includes(fromParam);
    }

    // On other paths, check 'section' in options
    if (options.section) {
      return this.skipVariantsCheckForSections.includes(options.section);
    }

    return false;
  }


  shouldShowPregnancyBottomSheet(data: {
    marketingTags: Array<ValueOf<typeof ApiClientConstant.User.MarketingTags>>,
    gender: ValueOf<typeof ApiClientConstant.User.Gender>,
    safteyProfile: string,
    overridePregnancyCheck?: boolean,
    section: string
  }): { show: boolean; activeTag?: ActivePregnancyTag } {
    const restrictedShopExperiment = this.experimentService.getRestrictedShopExperiment();
    const { marketingTags, gender, safteyProfile, overridePregnancyCheck, section } = data;

    const forbiddenSections = [this.appConfig.Shared.Shop.Sections.BOGO, this.appConfig.Shared.Shop.Sections.PERSONALIZED_BOGO, this.appConfig.Shared.Shop.Sections.REORDER].includes(section);
    if (forbiddenSections) return { show: false };
    if (!safteyProfile) return { show: false };
    if (!restrictedShopExperiment?.variant?.pregnancy) return { show: false };
    if (overridePregnancyCheck) return { show: false };

    const activeTag = this.userService.getActivePregnancyTag(marketingTags);
    if (!activeTag) return { show: false };

    const isFemaleGender = gender === ApiClientConstant.User.Gender.FEMALE;
    if (!isFemaleGender) return { show: false };

    const tagSafetyProfiles = restrictedShopExperiment?.variant?.[activeTag] || [];
    const isRestrictedProduct = tagSafetyProfiles.includes(safteyProfile);

    return isRestrictedProduct ? { show: true, activeTag } : { show: false };
  }

  shouldShowApprovalBottomSheet(data: {
    requiresApproval: string | undefined,
    overrideApprovalCheck: boolean,
    section: string,
  }): boolean {
    const { requiresApproval, overrideApprovalCheck, section } = data;
    if (overrideApprovalCheck) return false;

    const forbiddenSections = [this.appConfig.Shared.Shop.Sections.BOGO, this.appConfig.Shared.Shop.Sections.PERSONALIZED_BOGO, this.appConfig.Shared.Shop.Sections.REORDER].includes(section);
    if (forbiddenSections) return false;

    const restrictedShopExperiment = this.experimentService.getRestrictedShopExperiment();
    if (!restrictedShopExperiment?.enable) return false;
    if (!restrictedShopExperiment?.variant?.approval) return false;

    return requiresApproval === ApiClientConstant.Catalog.RequiresApprovalType.ALWAYS;
  }

  shouldShowTeenagerBottomSheet(data: {
    age: number,
    safteyProfile: string,
    section: string
  }): boolean {
    const { safteyProfile, section } = data;
    let { age } = data;
    if (typeof age === 'string') {
      age = Number.parseInt(age, 10);
    }

    const forbiddenSections = [this.appConfig.Shared.Shop.Sections.BOGO, this.appConfig.Shared.Shop.Sections.PERSONALIZED_BOGO, this.appConfig.Shared.Shop.Sections.REORDER, this.appConfig.Shared.Shop.Sections.REORDER].includes(section);
    if (forbiddenSections) return false;

    const restrictedShopExperiment = this.experimentService.getRestrictedShopExperiment();
    if (!restrictedShopExperiment?.variant?.teenager) return false;
    if (!age || Number.isNaN(age)) return false;
    if (age > this.appConfig.Shared.User.Age.TEENAGER_AGE) return false;

    const isTeenagerRestrictedProduct = restrictedShopExperiment?.variant?.teenager_safety_profile?.includes(safteyProfile);

    return isTeenagerRestrictedProduct;
  }

  private async handleAddProduct(
    product: any,
    quantity: number,
    options: CartUpdateOptions
  ): Promise<CartUpdateResult> {
    if (options.img) {
      this.animateAddToCart(options.img, options.flyToSelector);
    }
    const newQuantity = quantity + 1;
    options.productsInCart[product.objectId] = newQuantity;
    const params = this.buildCartParams(product, options);
    const cart = await this.conn.addProductToNewCart(params);

    // Broadcast cart update event so parent components can refresh their cart state
    this.broadcast.broadcast('CART_UPDATED', { productId: product.objectId, action: 'add' });

    return this.createSuccessResult(newQuantity, options.productsInCart, cart?.aov);
  }

  private async handleRemoveProduct(
    product: any,
    quantity: number,
    options: CartUpdateOptions
  ): Promise<CartUpdateResult> {
    try {
      const newQuantity = quantity - 1;
      options.productsInCart[product.objectId] = newQuantity;
      const cart = await this.conn.removeProductFromNewCart({
        productId: product.objectId,
        quantity: 1
      });

      // Broadcast cart update event so parent components can refresh their cart state
      this.broadcast.broadcast('CART_UPDATED', { productId: product.objectId, action: 'remove' });

      return this.createSuccessResult(newQuantity, options.productsInCart, cart?.aov);
    } catch (error) {
      return this.createFailureResult(quantity, options.productsInCart);
    }
  }

  private buildCartParams(product: any, options: CartUpdateOptions): any {
    const arr1 = Object.keys(options.productsInCart);
    const commonElements = options.cartItems?.filter((element: any) =>
      arr1.includes(element.productId)) || [];

    const tempData = { ...options.productsInCart };
    commonElements.forEach((each: any) => {
      tempData[each.productId] = each.quantity;
    });

    const params: any = {
      productId: product.objectId,
      quantity: tempData[product.objectId] ? 1 : 0,
      section: options.section || 'unknown',
    };

    return params;
  }

  private calculateDiscountForItems(
    cashBalance: number,
    cartItems: any[]
  ): { totalMRP: number, totalSP: number, discountPercentage: number } {
    const totals = cartItems.reduce((acc, item) => ({
      originalSP: acc.originalSP + (item.quantity * item.price),
      totalMRP: acc.totalMRP + (item.quantity * item.mrp)
    }), { originalSP: 0, totalMRP: 0 });

    const { originalSP, totalMRP } = totals;
    const flooredTotalMRP = Math.floor(totalMRP);
    const flooredOriginalSP = Math.floor(originalSP);

    const cashDiscount = Math.min(cashBalance, flooredOriginalSP);
    const totalSP = flooredOriginalSP - cashDiscount;
    const discountPercentage = this.getDiscountPercentage(flooredTotalMRP, totalSP);
    return { totalMRP: flooredTotalMRP, totalSP, discountPercentage };
  }

  private getDiscountPercentage(totalMRP: number, totalSP: number): number {
    return totalMRP > totalSP ? Math.floor(((totalMRP - totalSP) * 100) / totalMRP) : 0;
  }

  private handleEmptyCart(aov?: AovResponse): CartPricingResult {
    return {
      totalMRP: 0,
      totalSP: 0,
      discountPercentage: 0,
      aov,
      cartItems: []
    };
  }

  private createSuccessResult(
    quantity: number,
    productsInCart: { [key: string]: number },
    aov?: AovResponse,
  ): CartUpdateResult {
    return {
      success: true,
      updatedQuantity: quantity,
      shouldUpdateStore: true,
      updatedProductsInCart: productsInCart,
      aov,
    };
  }

  private createFailureResult(quantity: number, productsInCart: { [key: string]: number }): CartUpdateResult {
    return {
      success: false,
      updatedQuantity: quantity,
      shouldUpdateStore: false,
      updatedProductsInCart: productsInCart
    };
  }
}
